// DocumentImportDialog.js
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentService from "@/services/DocumentService";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import posthog from "posthog-js";
import { ReloadIcon } from "@radix-ui/react-icons";
import useStore from "@/store";
import languageNames from "@/constants/languageNames";

import "/node_modules/flag-icons/css/flag-icons.min.css";
import { LEARNING_LANGUAGE_OPTIONS, DEFAULT_LEARNING_LANGUAGE } from "@/constants/langcraftLanguages";

const DocumentImportDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { setCurrentCollectionId } = useStore().user;
  const { setCurrentCollectionById } = useStore().collections;
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("Untitled");
  const [language, setLanguage] = useState(DEFAULT_LEARNING_LANGUAGE);
  const [isCreating, setIsCreating] = useState(false);

  function mapLanguageToCountry(language) {
    // ... mapLanguageToCountry function implementation ...
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleCreateCollection = async () => {
    if (!file) {
      toast("Error", {
        description: "Please select a file to upload.",
        action: {
          label: "Close",
          onClick: () => console.log("Error Close"),
        },
      });
      return;
    }

    try {
      setIsCreating(true);
      const collectionDetails = {
        title,
        language,
        file,
        type: "document",
        contents: [],
      };

      const createdCollection =
        await DocumentService.createCollectionFromDocument(
          collectionDetails.title,
          collectionDetails.language,
          collectionDetails.file
        );

      toast("Success", {
        description: "The collection was created successfully!",
        action: {
          label: "Close",
          onClick: () => console.log("Close"),
        },
      });
      posthog.capture("collection_created");
      await setCurrentCollectionById(createdCollection.id);
      await setCurrentCollectionId(createdCollection.id);
      navigate("/");
    } catch (error) {
      console.error("Failed to create collection:", error);
      toast("Error", {
        description: `Failed to create collection: ${error.response.data.error}`,
        action: {
          label: "Close",
          onClick: () => console.log("Error Close"),
        },
      });
    } finally {
      setIsCreating(false);
      setFile(null);
      setTitle("Untitled");
      setLanguage(DEFAULT_LEARNING_LANGUAGE);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl rounded-[28px] border border-line bg-surface p-6 text-fg shadow-panel backdrop-blur-2xl">
        <div className="space-y-6">
          <div className="space-y-2">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-fg">
              Import From Document
            </DialogTitle>
            <p className="text-sm text-fg-muted">
              Upload a supported file to turn its contents into cards.
            </p>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="title"
                className="text-sm font-medium text-fg-muted"
              >
                Title
              </Label>
              <Input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                className="h-12 w-full rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg placeholder:text-fg-subtle transition focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="document"
                className="text-sm font-medium text-fg-muted"
              >
                Document
              </Label>
              <Input
                id="document"
                type="file"
                onChange={handleFileChange}
                className="h-12 w-full cursor-pointer rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg transition file:mr-4 file:rounded-xl file:border file:border-line file:bg-surface/80 file:px-4 file:py-2 file:text-sm file:font-medium file:text-fg file:transition file:hover:border-primary/40 file:hover:bg-elevated"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="language"
                className="text-sm font-medium text-fg-muted"
              >
                Language
              </Label>
              <Select
                id="language"
                value={language}
                onValueChange={(value) => setLanguage(value)}
              >
                <SelectTrigger className="h-12 rounded-2xl border border-line bg-surface/80 px-4 text-sm text-fg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEARNING_LANGUAGE_OPTIONS.map(({ code, label, flag, isDialect }) => (
                  <SelectItem key={code} value={code} className={isDialect ? "pl-7" : undefined}>
                    {flag} {label}
                  </SelectItem>
                ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-center pt-2">
            <Button
              className="flex w-full max-w-[200px] items-center justify-center rounded-full border border-primary/50 bg-gradient-to-r from-primary to-primary px-6 py-3 text-sm font-semibold text-primary-fg shadow-panel transition hover:shadow-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              onClick={handleCreateCollection}
              disabled={isCreating}
            >
              {isCreating ? (
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentImportDialog;
